import {
  CodeCommitClient,
  GetCommitCommand,
  GetBranchCommand,
  BatchGetCommitsCommand,
} from "@aws-sdk/client-codecommit";
import { Tables, putItem, scanAll, getItem, updateItem } from "./db";
import type { Commit } from "../types";

const REGION = "us-east-1";
const REPO_NAME = "pos-system";

/**
 * Parse CodeCommit's date format ("1781394754 -0700" or epoch string) to ISO string.
 */
function parseCodeCommitDate(dateStr: string | undefined): string {
  if (!dateStr) return new Date().toISOString();

  // Try parsing as "epoch offset" format (e.g., "1781394754 -0700")
  const parts = dateStr.trim().split(" ");
  if (parts.length >= 1) {
    const epoch = parseInt(parts[0], 10);
    if (!isNaN(epoch) && epoch > 1000000000) {
      return new Date(epoch * 1000).toISOString();
    }
  }

  // Try as ISO string directly
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) return d.toISOString();

  return new Date().toISOString();
}

let codeCommitClient: CodeCommitClient | null = null;

export function configureCodeCommit(credentials: {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
}) {
  codeCommitClient = new CodeCommitClient({ region: REGION, credentials });
}

function getCodeCommitClient(): CodeCommitClient {
  if (!codeCommitClient) {
    codeCommitClient = new CodeCommitClient({ region: REGION });
  }
  return codeCommitClient;
}

/** Fetch latest commits from CodeCommit that we haven't tracked yet */
export async function syncCommitsFromCodeCommit(limit = 20): Promise<Commit[]> {
  const cc = getCodeCommitClient();

  // Get the latest commit on main
  const branchResp = await cc.send(
    new GetBranchCommand({ repositoryName: REPO_NAME, branchName: "main" })
  );

  const tipCommitId = branchResp.branch?.commitId;
  if (!tipCommitId) return [];

  // Walk the commit history
  const commitIds: string[] = [];
  let currentId: string | undefined = tipCommitId;

  for (let i = 0; i < limit && currentId; i++) {
    commitIds.push(currentId);
    const getCommitResp: any = await cc.send(
      new GetCommitCommand({ repositoryName: REPO_NAME, commitId: currentId })
    );
    // Move to parent (first parent for linear history)
    currentId = getCommitResp.commit?.parents?.[0];
  }

  // Check which ones we already have
  const existingCommits = await getAllCommits();
  const existingIds = new Set(existingCommits.map((c) => c.commitId));
  const newCommitIds = commitIds.filter((id) => !existingIds.has(id));

  if (newCommitIds.length === 0) return existingCommits;

  // Batch get the new commits
  const batchResp = await cc.send(
    new BatchGetCommitsCommand({ repositoryName: REPO_NAME, commitIds: newCommitIds })
  );

  const newCommits: Commit[] = (batchResp.commits || []).map((c) => ({
    commitId: c.commitId || "",
    repositoryName: REPO_NAME,
    message: c.message?.trim() || "",
    author: c.author?.name || c.author?.email || "unknown",
    timestamp: parseCodeCommitDate(c.author?.date),
    linkedFeatures: [],
    linkedBugs: [],
    reviewStatus: "pending" as const,
  }));

  // Persist new commits to DynamoDB
  for (const commit of newCommits) {
    await putItem(Tables.COMMITS, commit);
  }

  return [...newCommits, ...existingCommits].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

/** Get all tracked commits from DynamoDB */
export async function getAllCommits(): Promise<Commit[]> {
  const items = await scanAll(Tables.COMMITS);
  return (items as Commit[]).sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

/** Get a single commit */
export async function getCommit(commitId: string): Promise<Commit | undefined> {
  const item = await getItem(Tables.COMMITS, { commitId });
  return item as Commit | undefined;
}

/** Link a commit to a feature request */
export async function linkCommitToFeature(commitId: string, featureId: string): Promise<void> {
  const commit = await getCommit(commitId);
  if (!commit) return;

  const features = commit.linkedFeatures.includes(featureId)
    ? commit.linkedFeatures
    : [...commit.linkedFeatures, featureId];

  await updateItem(Tables.COMMITS, { commitId }, { linkedFeatures: features });
}

/** Link a commit to a bug report */
export async function linkCommitToBug(commitId: string, bugId: string): Promise<void> {
  const commit = await getCommit(commitId);
  if (!commit) return;

  const bugs = commit.linkedBugs.includes(bugId)
    ? commit.linkedBugs
    : [...commit.linkedBugs, bugId];

  await updateItem(Tables.COMMITS, { commitId }, { linkedBugs: bugs });
}

/** Get commits not yet assigned to any version */
export async function getUnversionedCommits(versions: { commits: string[] }[]): Promise<Commit[]> {
  const allCommits = await getAllCommits();
  const versionedIds = new Set(versions.flatMap((v) => v.commits));
  return allCommits.filter((c) => !versionedIds.has(c.commitId));
}
